import type { ComponentType, ReactElement, ReactNode } from 'react';

import type { CustomBubbleProps, CustomMessageProps, ICustomMessage } from '@/components/chat';
import { ChatBubbleErrorBoundary } from '@/components/chat/bubbles/ChatBubbleErrorBoundary';
import type { RenderContext } from '@/components/chat/message-list/RenderContext';

/**
 * Declarative description of how one message type renders.
 *
 * Every message type renders as a **row** or as a **bubble**. There is no third
 * category and no per-type exception. That is the grain of the library — the
 * two are exactly GiftedChat's `renderMessage` and `renderBubble`, and its
 * `Message` is what renders one inside the other.
 *
 * A row replaces the entire message row: no avatar, no alignment-by-author, no
 * caption. A bubble replaces only the bubble inside an otherwise normal row,
 * which keeps all of those. The distinction is real, not bookkeeping: a shared
 * reference is attributed to a person and keeps its avatar; a content
 * suggestion is system-authored and has none.
 *
 * The rule governs how a *message* renders. It says nothing about row furniture
 * — the avatar, the timestamp, the caption, a status indicator — which are not
 * message types and never were.
 *
 * An entry states what it needs (who sees it, which slot, how to build props)
 * instead of adding a branch to a dispatcher, so both dispatchers stay loops.
 */

export type BubbleContext = RenderContext;

/**
 * Who sees the bubble. `submitter` is the inquiry author; `recipient` is the
 * other participant. Both viewers share one Twilio conversation, so a
 * one-sided bubble must be gated here rather than by message authorship.
 */
export type BubbleVisibility =
  | 'both'
  | 'submitter'
  | 'recipient'
  | ((message: ICustomMessage, ctx: BubbleContext) => boolean);

/** Replaces the whole message row. */
export type RowContainer = {
  kind: 'row';
  position?: (message: ICustomMessage, ctx: BubbleContext) => 'left' | 'right';
};

/**
 * Replaces the bubble inside an otherwise normal row.
 *
 * `fillRow` is read by the row level *before* the bubble level runs, so the row
 * can size itself for what is about to render. Both levels resolve it from the
 * same entry, which is what stops them disagreeing.
 */
export type BubbleContainer = { kind: 'bubble'; fillRow?: boolean };

/**
 * What a bubble degrades to when it throws.
 *
 * `'default'` renders the message through the dispatcher's ordinary path — the
 * plain row and bubble it would have got without any entry. Prefer it: it is
 * the only option guaranteed to share almost no code with the bubble that just
 * failed.
 *
 * A `ReactNode` or a function is for bubbles the default path cannot represent
 * — typically ones that carry no message text of their own.
 */
export type BubbleFallback =
  | 'default'
  | ReactNode
  | ((message: ICustomMessage, ctx: BubbleContext) => ReactNode);

export type BubbleErrorBoundaryConfig = {
  /**
   * Sentry `bubbleVariant` tag. Stated per entry rather than defaulting to the
   * entry's `type`, because an entry whose `match` claims more than one type
   * would otherwise tag every one of them with a single type's name.
   */
  variant: string;
  /**
   * Rendered when the bubble throws. Required, so every entry states its intent.
   *
   * Choose by what the bubble *depends on*, not by what it renders. A boundary
   * renders its fallback inside its own render, so a fallback that throws is
   * NOT caught — it escapes to the conversation-level boundary and takes the
   * thread down, which is the outcome this boundary exists to prevent. A
   * fallback rebuilt from the same primitives as a thin bubble therefore buys
   * nothing: it shares the failure.
   *
   * Also choose by what the user is doing. A passive card can vanish. An
   * interactive control someone is part-way through should say something.
   */
  fallback: BubbleFallback;
};

type EntryBase<Props, RenderProps, Container> = {
  /** Discriminator this entry claims, and its default match. */
  type: string;
  /**
   * Overrides the default type match. Needed when a bubble claims messages by
   * something other than their discriminator — an entry with a `match` must be
   * ordered ahead of any entry its predicate could shadow.
   */
  match?: (message: ICustomMessage, ctx: BubbleContext) => boolean;
  visibility: BubbleVisibility;
  container: Container;
  errorBoundary: BubbleErrorBoundaryConfig;
  /**
   * Maps the message, context, and the dispatcher's per-render props to the
   * component's props. Returning `null` means "this message can't paint the
   * bubble" — a malformed or incomplete payload — and the caller falls through
   * to plain text. It must never throw: the tolerant read path is what keeps a
   * bad payload from taking down the thread, and this is the last place that
   * guarantee is cheap to keep.
   *
   * This is the only function on an entry that varies per render. Visibility,
   * container, and error-boundary config are declared values, deliberately.
   */
  toProps: (message: ICustomMessage, ctx: BubbleContext, renderProps: RenderProps) => Props | null;
  Component: ComponentType<Props>;
};

export type RowEntry<Props> = EntryBase<Props, CustomMessageProps, RowContainer>;

export type BubbleEntry<Props> = EntryBase<Props, CustomBubbleProps, BubbleContainer>;

/**
 * Props-erased entries, so entries with unrelated component props share one
 * ordered list. The `define*` helpers are the only place the erasure happens,
 * and they type-check each entry against its real props on the way in.
 *
 * Rows and bubbles are held in separate sets rather than one discriminated
 * list, because the two dispatchers hand down different per-render props and a
 * single set could not type `toProps` for both without casting the difference
 * away.
 */
export type AnyRowEntry = RowEntry<Record<string, unknown>>;

export type AnyBubbleEntry = BubbleEntry<Record<string, unknown>>;

export function defineRowEntry<Props>(entry: RowEntry<Props>): AnyRowEntry {
  return entry as unknown as AnyRowEntry;
}

export function defineBubbleEntry<Props>(entry: BubbleEntry<Props>): AnyBubbleEntry {
  return entry as unknown as AnyBubbleEntry;
}

/**
 * What the registry decided for a message.
 *
 * `none` and `fallthrough` are deliberately distinct: `none` means no entry
 * claimed the message and the caller should keep looking, while `fallthrough`
 * means an entry claimed it but its payload was unusable, so the caller must
 * render plain text and stop.
 */
export type BubbleResolution =
  | { kind: 'none' }
  | { kind: 'hidden' }
  | { kind: 'fallthrough' }
  | { kind: 'element'; element: ReactElement };

function isVisible(
  visibility: BubbleVisibility,
  message: ICustomMessage,
  ctx: BubbleContext
): boolean {
  if (typeof visibility === 'function') {
    return visibility(message, ctx);
  }

  if (visibility === 'submitter') {
    return ctx.isAuthor;
  }

  if (visibility === 'recipient') {
    return !ctx.isAuthor;
  }

  return true;
}

function resolveFallback(
  fallback: BubbleFallback,
  message: ICustomMessage,
  ctx: BubbleContext,
  defaultElement: ReactNode | undefined
): ReactNode {
  if (fallback === 'default') {
    return defaultElement ?? null;
  }

  if (typeof fallback === 'function') {
    return (fallback as (m: ICustomMessage, c: BubbleContext) => ReactNode)(message, ctx);
  }

  return fallback ?? null;
}

type MatchableEntry = {
  type: string;
  match?: (message: ICustomMessage, ctx: BubbleContext) => boolean;
};

/**
 * First entry in the set that claims the message. Order is significant and
 * load-bearing — see `match`.
 */
export function resolveEntry<Entry extends MatchableEntry>(
  entries: readonly Entry[],
  message: ICustomMessage,
  ctx: BubbleContext
): Entry | undefined {
  return entries.find((entry) =>
    entry.match ? entry.match(message, ctx) : message?.type === entry.type
  );
}

/**
 * Runs one entry: visibility gate, then props, then the component wrapped in an
 * error boundary. Every registered bubble gets a boundary, so a throwing bubble
 * degrades to its fallback instead of taking the conversation down with it.
 */
export function applyEntry<RenderProps>(
  entry: EntryBase<Record<string, unknown>, RenderProps, RowContainer | BubbleContainer>,
  message: ICustomMessage,
  ctx: BubbleContext,
  renderProps: RenderProps,
  /** The dispatcher's ordinary rendering of this message, for `fallback: 'default'`. */
  defaultElement?: ReactNode
): BubbleResolution {
  if (!isVisible(entry.visibility, message, ctx)) {
    return { kind: 'hidden' };
  }

  const props = entry.toProps(message, ctx, renderProps);

  if (props === null) {
    return { kind: 'fallthrough' };
  }

  const { Component, errorBoundary } = entry;

  const fallback = resolveFallback(errorBoundary.fallback, message, ctx, defaultElement);

  return {
    kind: 'element',
    element: (
      <ChatBubbleErrorBoundary
        variant={errorBoundary.variant}
        messageSid={message.messageId}
        conversationId={ctx.conversation.id}
        fallback={fallback}>
        <Component {...props} />
      </ChatBubbleErrorBoundary>
    ),
  };
}

/**
 * Whether the bubble that will render wants its row widened.
 *
 * The row commits to its width before the bubble level runs, so the row asks
 * the same question here that the bubble dispatcher answers later. Both read
 * one entry, which is what makes them agree by construction rather than by two
 * call sites staying in sync.
 */
export function resolveFillRow(
  entries: readonly AnyBubbleEntry[],
  message: ICustomMessage,
  ctx: BubbleContext
): boolean {
  const entry = resolveEntry(entries, message, ctx);

  if (!entry) {
    return false;
  }

  return Boolean(entry.container.fillRow) && isVisible(entry.visibility, message, ctx);
}
